import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import type { Theme } from '@mui/material';
import { Box, Popover as MuiPopper } from '@mui/material';
import {
  createContext,
  type MouseEvent,
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Popover } from '@/components/AutoComplete/AutoCompleteExcelLike/components/Popover';

const autoCompleteStyles = {
  wrapperAutoCompleteExcelLike: (theme: Theme) => ({
    width: '100%',
    height: '36px',
    padding: '8px',
    lineHeight: 1,
    fontSize: theme.fontSizes.smaller2,
  }),
  box: (theme: Theme) => ({
    position: 'relative',
    width: '100%',
    height: '100%',
    minHeight: '2.25rem',
    boxShadow:
      '0 1px 5px 0 rgba(0, 0, 0, 0.2), 0 3px 1px -2px rgba(0, 0, 0, 0.12), 0 2px 2px 0 rgba(0, 0, 0, 0.14)',
    borderRadius: '5px',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.palette.background.default,
    textAlign: 'left',
    border: 'none',
    padding: 0,
    fontSize: theme.fontSizes.smaller2,
    '&:hover': {
      cursor: 'pointer',
    },
    '&:disabled': {
      cursor: 'auto',
      backgroundColor: theme.palette.primaryBackground.disabled,
    },
  }),
  boxOpen: (theme: Theme) => ({
    backgroundColor: theme.palette.baseColors.white.c100,
  }),
  label: (theme: Theme) => ({
    flexShrink: 1,
    flexGrow: 1,
    fontFamily: theme.fonts.base,
    textTransform: 'uppercase',
    fontWeight: 'bold',
    paddingLeft: '12px',
    color: theme.palette.text.primary,
    '&[disabled]': {
      color: theme.palette.text.disabled,
    },
  }),
  rightPart: {
    display: 'flex',
    flexShrink: 0,
    flexGrow: 0,
    alignItems: 'center',
    paddingRight: '8px',
    paddingLeft: '8px',
  },
  arrowOpen: {
    transform: 'rotate(180deg)',
  },
  popper: {
    minWidth: '400px',
  },
  popperAlignment: {
    '& > .MuiPaper-root': {
      transform: 'translateX(-8px) !important',
    },
  },
  chosen: (theme: Theme) => ({
    backgroundColor: '#2A2E49',
    color: 'white',
    borderRadius: '25px',
    width: '20px',
    height: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontFamily: theme.fonts.base,
    fontSize: theme.fontSizes.smallest,
  }),
};

export type BaseOption = {
  id: string | number;
  [key: string]: unknown;
};

type OptionWithSelection<T extends BaseOption = BaseOption> = T & {
  isChosen: boolean;
};

type AutoCompleteContextValue<T extends BaseOption = BaseOption> = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  anchorEl: HTMLElement | null;
  setAnchorEl: (element: HTMLElement | null) => void;
  top: number | null;
  offsetPopper: boolean;
  setOffsetPopper: (offset: boolean) => void;
  handleClose: () => void;
  options: OptionWithSelection<T>[] | null;
  setOptions: (options: OptionWithSelection<T>[]) => void;
  numberOfChosenOptions: number;
  heightSizingRef: RefObject<HTMLDivElement>;
};

const AutoCompleteContext = createContext<AutoCompleteContextValue | null>(null);

const useAutoCompleteContext = () => {
  const context = useContext(AutoCompleteContext);
  if (!context) {
    throw new Error('AutoComplete compound components must be used within AutoComplete.Root');
  }
  return context;
};

// Component prop types
type RootProps<T extends BaseOption = BaseOption> = {
  value?: T[];
  onChange?: (selectedOptions: T[]) => void;
  options?: T[];
  children: ReactNode;
  dataId?: string;
};

type TriggerProps = {
  children?: ReactNode;
  disabled?: boolean;
};

type ContentProps = {
  children: ReactNode;
};

type GroupOptionsProps = {
  groupOptionKey?: string | string[];
  groupsOpenedByDefault?: boolean;
  groupByName?: string | string[];
};

type LoadingProps = {
  children?: ReactNode;
  isLoading?: boolean;
};

type BadgeProps = {
  children?: ReactNode;
};

type IconProps = {
  children?: ReactNode;
};

const Root = <T extends BaseOption = BaseOption>({
  value = [],
  onChange,
  options = [],
  children,
  dataId,
}: RootProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [top, setTop] = useState<number | null>(null);
  const [offsetPopper, setOffsetPopper] = useState(false);
  const heightSizingRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (heightSizingRef.current) {
      const { top, height } = heightSizingRef.current.getBoundingClientRect();
      setTop(top + height);
    }
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleOptionsChange = useCallback(
    (newOptions: OptionWithSelection<T>[]) => {
      const selectedOptions = newOptions.filter(({ isChosen }) => !!isChosen) as T[];
      onChange?.(selectedOptions);
    },
    [onChange],
  );

  const valueArray = Array.isArray(value) ? value : [];
  const mergedOptions =
    options?.map(option => ({
      ...option,
      isChosen: valueArray.some(selectedOption => selectedOption?.id === option?.id),
    })) || null;

  const numberOfChosenOptions = useMemo(() => {
    return (mergedOptions || []).filter(({ isChosen }) => !!isChosen).length;
  }, [mergedOptions]);

  const contextValue: AutoCompleteContextValue<T> = {
    isOpen,
    setIsOpen,
    anchorEl,
    setAnchorEl,
    top,
    offsetPopper,
    setOffsetPopper,
    handleClose,
    options: mergedOptions,
    setOptions: handleOptionsChange,
    numberOfChosenOptions,
    heightSizingRef,
  };

  return (
    <AutoCompleteContext.Provider value={contextValue as unknown as AutoCompleteContextValue}>
      <Box data-id={dataId ?? 'autocomplete-excel-root'}>{children}</Box>
    </AutoCompleteContext.Provider>
  );
};

const Trigger = ({ children, disabled = false }: TriggerProps) => {
  const { isOpen, setIsOpen, setAnchorEl, setOffsetPopper, heightSizingRef } =
    useAutoCompleteContext();

  const styles = autoCompleteStyles;

  const handleClick = (e: MouseEvent<HTMLElement>) => {
    setIsOpen(!isOpen);
    setAnchorEl(e.currentTarget);
    const buttonOffsetLeft = e.currentTarget.getBoundingClientRect().left;
    setOffsetPopper(buttonOffsetLeft === 8);
  };

  return (
    <Box ref={heightSizingRef} data-id='autocomplete-excel-trigger-wrapper'>
      <Box
        component='button'
        type='button'
        onClick={handleClick}
        sx={[styles.box, isOpen ? styles.boxOpen : {}]}
        disabled={disabled}
        data-id='autocomplete-excel-trigger'
      >
        {children}
        <ArrowDropDown sx={[isOpen ? styles.arrowOpen : {}]} />
      </Box>
    </Box>
  );
};

const NumberOfChosenOptions = () => {
  const { numberOfChosenOptions } = useAutoCompleteContext();

  const styles = autoCompleteStyles;

  if (!numberOfChosenOptions) {
    return null;
  }

  return (
    <Box sx={styles.rightPart} data-id='autocomplete-excel-count'>
      <Box component='span' sx={styles.chosen} data-id='autocomplete-excel-count-value'>
        {numberOfChosenOptions}
      </Box>
    </Box>
  );
};

const Content = ({ children }: ContentProps) => {
  const { isOpen, anchorEl, offsetPopper, handleClose } = useAutoCompleteContext();

  const styles = autoCompleteStyles;

  return (
    <MuiPopper
      sx={[styles.popper, offsetPopper ? styles.popperAlignment : {}]}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      keepMounted
      open={isOpen}
      onClose={handleClose}
      data-id='autocomplete-excel-content'
    >
      {children}
    </MuiPopper>
  );
};

const GroupOptions = ({
  groupOptionKey,
  groupsOpenedByDefault = true,
  groupByName,
}: GroupOptionsProps) => {
  const { top, options, setOptions } = useAutoCompleteContext();

  return (
    <Popover
      top={top}
      options={options || []}
      setOptions={setOptions}
      loading={false}
      groupOptionKey={groupOptionKey}
      label=''
      groupsOpenedByDefault={groupsOpenedByDefault}
      groupByName={groupByName}
      groupSorter={null}
      entitiesSorter={null}
    />
  );
};

const Loading = ({ children, isLoading = false }: LoadingProps) => {
  if (!isLoading) return null;
  return <>{children ?? <div>Loading...</div>}</>;
};

type LabelProps = React.ComponentPropsWithoutRef<'div'>;

const Label = ({ children }: LabelProps) => {
  const styles = autoCompleteStyles;
  return (
    <Box component='div' sx={styles.label} data-id='autocomplete-excel-label'>
      {children}
    </Box>
  );
};

const Badge = ({ children }: BadgeProps) => {
  const { numberOfChosenOptions } = useAutoCompleteContext();
  const styles = autoCompleteStyles;

  if (!numberOfChosenOptions) return null;

  return (
    <Box component='span' sx={styles.chosen} data-id='autocomplete-excel-badge'>
      {children ?? numberOfChosenOptions}
    </Box>
  );
};

const Icon = ({ children }: IconProps) => {
  const { isOpen } = useAutoCompleteContext();

  const styles = autoCompleteStyles;

  return (
    <Box sx={[isOpen ? styles.arrowOpen : {}]} data-id='autocomplete-excel-icon'>
      {children ?? <ArrowDropDown />}
    </Box>
  );
};

export const AutoCompleteComposable = {
  Root,
  Trigger,
  Content,
  GroupOptions,
  Loading,
  Label,
  Badge,
  Icon,
  NumberOfChosenOptions,
};
